import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Subscription, of } from 'rxjs';
import { timeout, catchError } from 'rxjs/operators';

import size from 'lodash/size';
import has from 'lodash/has';

@Injectable({
  providedIn: 'root',
})
export class CommsService {
  /** The default request timeout */
  private _timeoutCounter = 60000;

  constructor(public http: HttpClient) {}

  /**
   * Constructs a `GET` request
   *
   * @param url     The endpoint URL.
   * @param options The HTTP options to send with the request.
   * @param successHandler The success callback. This is
   * @param failHandler Optional. The failed request callback. The successHandler callback will becalled if this is not provided.
   * @param timeoutCounter Optional. The timeout timer for the request.
   *
   * @return The subscription from the HTTP request
   */
  public get(
    url: string,
    options: any,
    successHandler: (payload) => void,
    failHandler?: (err) => void,
    timeoutCounter?: number,
  ): Subscription {
    if (!timeoutCounter) {
      timeoutCounter = this._timeoutCounter;
    }

    return this.http
      .get(url, options)
      .pipe(
        timeout(timeoutCounter),
        catchError((error, caught) =>
          of({
            error: error,
            caught: caught,
            message: error?.message || 'Oops! Something went wrong',
          }),
        ),
      )
      .subscribe(payload => {
        if (
          payload &&
          ((has(payload, 'error') && has(payload, 'caught')) ||
            payload instanceof Error ||
            payload instanceof HttpErrorResponse)
        ) {
          if (failHandler) {
            failHandler(payload);
          } else {
            successHandler(false);
          }
        } else {
          successHandler(payload);
        }
      });
  }

  /**
   * Constructs a `POST` request
   *
   * @param url     The endpoint URL.
   * @param body    The HTTP options to send with the request.
   * @param options HTTP options.
   * @param successHandler The success callback. This is
   * @param failHandler Optional. The failed request callback. The successHandler callback will becalled if this is not provided.
   * @param timeoutCounter Optional. The timeout timer for the request.
   *
   * @return An `Observable` of the response, with the response body as an `ArrayBuffer`.
   */
  public post(
    url: string,
    body: any,
    options: any,
    successHandler: (payload) => void,
    failHandler?: (err) => void,
    timeoutCounter?: number,
  ): Subscription {
    if (!timeoutCounter) {
      timeoutCounter = this._timeoutCounter;
    }

    options = size(options) ? options : ({} as any);

    return this.http
      .post(url, body, options)
      .pipe(
        timeout(timeoutCounter),
        catchError((error, caught) =>
          of({
            error: error,
            caught: caught,
            message: error?.message || 'Something went wrong',
          }),
        ),
      )
      .subscribe(payload => {
        if (
          payload &&
          ((has(payload, 'error') && has(payload, 'caught')) ||
            payload instanceof Error ||
            payload instanceof HttpErrorResponse)
        ) {
          if (failHandler) {
            failHandler(payload);
          } else {
            successHandler(false);
          }
        } else {
          successHandler(payload);
        }
      });
  }

  /**
   * Constructs a `PUT` request
   *
   * @param url     The endpoint URL.
   * @param body    The HTTP options to send with the request.
   * @param options HTTP options.
   * @param successHandler The success callback. This is
   * @param failHandler Optional. The failed request callback. The successHandler callback will becalled if this is not provided.
   * @param timeoutCounter Optional. The timeout timer for the request.
   *
   * @return An `Observable` of the response, with the response body as an `ArrayBuffer`.
   */
  public put(
    url: string,
    body: any,
    options: any,
    successHandler: (payload) => void,
    failHandler?: (err) => void,
    timeoutCounter?: number,
  ): Subscription {
    if (!timeoutCounter) {
      timeoutCounter = this._timeoutCounter;
    }

    options = size(options) ? options : ({} as any);

    return this.http
      .put(url, body, options)
      .pipe(
        timeout(timeoutCounter),
        catchError((error, caught) =>
          of({
            error: error,
            caught: caught,
            message: error?.message || 'Something went wrong',
          }),
        ),
      )
      .subscribe(payload => {
        if (
          payload &&
          ((has(payload, 'error') && has(payload, 'caught')) ||
            payload instanceof Error ||
            payload instanceof HttpErrorResponse)
        ) {
          if (failHandler) {
            failHandler(payload);
          } else {
            successHandler(false);
          }
        } else {
          successHandler(payload);
        }
      });
  }
}
